import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Send, CheckCircle, Mail, MapPin, Phone, Loader2 } from "lucide-react";

export default function Contact() {
  useEffect(() => { document.title = "Contact Us — Acadence"; }, []);

  const [form, setForm] = useState({ name: "", email: "", phone: "", institute: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch("https://formsubmit.co/ajax/info@acadence.in", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          institute: form.institute,
          message: form.message,
          _subject: `New enquiry from ${form.name} — ${form.institute}`,
        }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Navbar */}
      <nav className="bg-[#0F1B3D] border-b border-[#1E2D55]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
              <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
              <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
              <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
            </svg>
            <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF]/50 hover:text-[#EEEADF] transition-colors">Features</Link>
            <Link to="/contact" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF] transition-colors">Contact</Link>
          </div>
          <Link to="/login" className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/60 hover:text-[#EEEADF] transition-colors px-4 py-2">
            Sign in
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24">
          {/* Left — Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#7A7468] mb-4">— Get in Touch</p>
            <h1 className="font-['Instrument_Serif'] text-[#0F1B3D] leading-[1.05] mb-6" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
              Let's digitize<br />
              <em className="italic text-[#4B7BF5]">your institute.</em>
            </h1>
            <p className="font-['DM_Sans'] text-[#7A7468] text-base leading-relaxed mb-12 max-w-md">
              Tell us about your institute and we'll show you how Acadence can streamline every process — from admissions to placements.
            </p>

            <div className="space-y-6">
              {[
                { icon: Mail, label: "Email", value: "info@acadence.in", href: "mailto:info@acadence.in" },
                { icon: Phone, label: "Phone", value: "+91 (call/WhatsApp)", href: null },
                { icon: MapPin, label: "Location", value: "India", href: null },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0F1B3D] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-[#4B7BF5]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-1">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="font-['DM_Sans'] text-sm text-[#0F1B3D] hover:text-[#4B7BF5] transition-colors">{item.value}</a>
                    ) : (
                      <p className="font-['DM_Sans'] text-sm text-[#0F1B3D]">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-[#D8D4CB]">
              <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-4">What happens next?</p>
              <div className="space-y-3">
                {[
                  "We'll reach out within 24 hours",
                  "Schedule a personalized demo",
                  "Get your institute live in days",
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="font-['JetBrains_Mono'] text-[10px] text-[#4B7BF5] w-5 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-['DM_Sans'] text-sm text-[#7A7468]">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="bg-white border border-[#E2DDD4] p-8 md:p-10">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-16 h-16 bg-[#22C55E]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-[#22C55E]" />
                    </div>
                    <h3 className="font-['Syne'] font-bold text-2xl text-[#0F1B3D] mb-3">Message sent!</h3>
                    <p className="font-['DM_Sans'] text-[#7A7468] text-sm mb-8 max-w-xs mx-auto">
                      We've received your enquiry and will get back to you within 24 hours.
                    </p>
                    <Link to="/" className="font-['Syne'] font-bold text-xs uppercase tracking-widest text-[#4B7BF5] hover:text-[#0F1B3D] transition-colors">
                      Back to Home
                    </Link>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    exit={{ opacity: 0 }}
                  >
                    <div>
                      <h2 className="font-['Syne'] font-bold text-xl text-[#0F1B3D] mb-1">Request a Demo</h2>
                      <p className="font-['DM_Sans'] text-sm text-[#7A7468]">Fill in your details and we'll set up a personalized walkthrough.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Your Name</label>
                        <input
                          type="text" name="name" value={form.name} onChange={handleChange} required
                          placeholder="Full name"
                          className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Phone</label>
                        <input
                          type="tel" name="phone" value={form.phone} onChange={handleChange}
                          placeholder="+91 98765..."
                          className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Email Address</label>
                      <input
                        type="email" name="email" value={form.email} onChange={handleChange} required
                        placeholder="you@institute.edu"
                        className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Institute Name</label>
                      <input
                        type="text" name="institute" value={form.institute} onChange={handleChange} required
                        placeholder="e.g. Mumbai University"
                        className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Message</label>
                      <textarea
                        name="message" value={form.message} onChange={handleChange} rows={4}
                        placeholder="Tell us about your institute and what you're looking for..."
                        className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-[#0F1B3D] text-[#4B7BF5] font-['Syne'] font-bold text-xs uppercase tracking-widest py-4 hover:bg-[#162044] transition-colors active:scale-95 disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Enquiry <Send className="w-4 h-4" /></>}
                    </button>

                    <p className="font-['DM_Sans'] text-[11px] text-[#BDBAA8] text-center">
                      Or email us directly at{" "}
                      <a href="mailto:info@acadence.in" className="text-[#4B7BF5] hover:text-[#0F1B3D] transition-colors">info@acadence.in</a>
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0F1B3D] border-t border-[#1E2D55] py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/><rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/><rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/></svg>
            <span className="font-['DM_Sans'] font-medium text-sm text-[#EEEADF] tracking-[0.08em]">acadence</span>
          </div>
          <p className="font-['DM_Sans'] text-[#EEEADF]/25 text-xs">© 2026 Acadence. All rights reserved.</p>
          <a href="mailto:info@acadence.in" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/30 hover:text-[#EEEADF] transition-colors">info@acadence.in</a>
        </div>
      </footer>
    </div>
  );
}
